import { useEffect, useRef, useState } from "react";
import AccountButton from "../AccountButton";
import AccountSection from "../AccountSection";
import {
  addFamilyHistory,
  deleteFamilyHistory,
  getFamilyHistory,
} from "../api/apiFunctions";
import { t } from "../i18n";
import { getAuthToken } from "../utils/auth";
import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";

export default function HistoryPage({ lang = "en", goToTab }) {
  const { dontHaveAccess } = useSubscriptionAccess();
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    condition: "",
    selectedRelations: [], // allow selecting multiple family members
  });
  const [chronicles, setChronicles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Medical conditions API search states
  const [conditionSearch, setConditionSearch] = useState("");
  const [conditionResults, setConditionResults] = useState([]);
  const [showConditionDropdown, setShowConditionDropdown] = useState(false);
  const [isConditionLoading, setIsConditionLoading] = useState(false);
  const conditionDropdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadFamilyHistory();
  }, []);

  // Helper function to extract family history data from API response
  const extractFamilyHistoryData = (response) => {
    try {
      if (Array.isArray(response)) {
        return response;
      }
      if (
        response &&
        typeof response === "object" &&
        response.success &&
        response.data
      ) {
        if (
          response.data.familyHistory &&
          Array.isArray(response.data.familyHistory)
        ) {
          return response.data.familyHistory;
        } else if (Array.isArray(response.data)) {
          return response.data;
        } else {
          return [];
        }
      }
      if (response && typeof response === "object" && response.data) {
        if (
          response.data.familyHistory &&
          Array.isArray(response.data.familyHistory)
        ) {
          return response.data.familyHistory;
        } else if (Array.isArray(response.data)) {
          return response.data;
        } else {
          return [];
        }
      }
      if (response && typeof response === "object") {
        return [];
      }
      return [];
    } catch (error) {
      console.error("Error extracting family history data:", error);
      return [];
    }
  };

  // Load family history from API
  const loadFamilyHistory = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await getFamilyHistory(token);
      const familyHistoryData = extractFamilyHistoryData(response);

      // Transform data to match expected format
      const transformedHistory = familyHistoryData.map((item) => {
        return {
          id: item._id || item.id,
          condition: item.condition || "",
          relation:
            item.relation || item.familyRelation || item.relationship || "",
          status: item.status || "active",
        };
      });

      setChronicles(transformedHistory);
    } catch (error) {
      console.error("Error loading family history:", error);
      setError(
        error.message || "Failed to load family history. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clicks outside condition dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        conditionDropdownRef.current &&
        !conditionDropdownRef.current.contains(event.target)
      ) {
        setShowConditionDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch medical conditions from NLM API
  useEffect(() => {
    const fetchConditions = async () => {
      if (!conditionSearch || conditionSearch.length < 2) {
        setConditionResults([]);
        setIsConditionLoading(false);
        return;
      }

      setIsConditionLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/conditions/v3/search?terms=${encodeURIComponent(
          conditionSearch,
        )}&count=5&df=consumer_name,primary_name&ef=icd10cm_codes,term_icd9_code,term_icd9_text`;
        const res = await fetch(url);
        const data = await res.json();

        // Parse the API response according to the documentation
        // data[3] contains the display fields (consumer_name, primary_name)
        // data[2] contains the extra fields (icd10cm_codes, term_icd9_code, term_icd9_text)
        const results = (data[3] || []).map((row, idx) => ({
          consumerName: row[0] || "",
          primaryName: row[1] || "",
          icd10cmCodes: data[2]?.icd10cm_codes?.[idx] || "",
          icd9Code: data[2]?.term_icd9_code?.[idx] || "",
          icd9Text: data[2]?.term_icd9_text?.[idx] || "",
        }));

        setConditionResults(results);
      } catch (e) {
        console.error("Error fetching conditions:", e);
        setConditionResults([]);
        setShowConditionDropdown(true);
      } finally {
        setIsConditionLoading(false);
      }
    };
    const timeout = setTimeout(fetchConditions, 400);
    return () => clearTimeout(timeout);
  }, [conditionSearch]);

  const relationOptions = [
    { value: "", label: t("chooseRelation", lang) },
    { value: "brother", label: t("brother", lang) || "Brother" },
    { value: "sister", label: t("sister", lang) || "Sister" },
    { value: "mother", label: t("mother", lang) },
    { value: "father", label: t("father", lang) },
    {
      value: "paternal_grandmother",
      label: t("paternalGrandmother", lang) || "Paternal Grandmother",
    },
    {
      value: "maternal_grandmother",
      label: t("maternalGrandmother", lang) || "Maternal Grandmother",
    },
    {
      value: "paternal_grandfather",
      label: t("paternalGrandfather", lang) || "Paternal Grandfather",
    },
    {
      value: "maternal_grandfather",
      label: t("maternalGrandfather", lang) || "Maternal Grandfather",
    },
    {
      value: "maternal_aunt",
      label: t("maternalAunt", lang) || "Maternal Aunt",
    },
    {
      value: "paternal_aunt",
      label: t("paternalAunt", lang) || "Paternal Aunt",
    },
    {
      value: "maternal_uncle",
      label: t("maternalUncle", lang) || "Maternal Uncle",
    },
    {
      value: "paternal_uncle",
      label: t("paternalUncle", lang) || "Paternal Uncle",
    },
  ];
  const statusOptions = [
    { value: "all", label: t("allStatuses", lang) },
    { value: "active", label: t("active", lang) },
    { value: "inactive", label: t("inactive", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);

  const handleRelationToggle = (value) => {
    setForm((prev) => {
      const already = prev.selectedRelations.includes(value);
      return {
        ...prev,
        selectedRelations: already
          ? prev.selectedRelations.filter((v) => v !== value)
          : [...prev.selectedRelations, value],
      };
    });
  };

  const handleConditionSearchChange = (e) => {
    const value = e.target.value;
    setConditionSearch(value);
    setForm((prev) => ({ ...prev, condition: value }));
    if (value.length >= 2) {
      setShowConditionDropdown(true);
    } else {
      setShowConditionDropdown(false);
    }
  };

  const handleConditionSelect = (condition) => {
    setForm((prev) => ({
      ...prev,
      condition: condition.consumerName || condition.primaryName,
    }));
    setConditionSearch("");
    setShowConditionDropdown(false);
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center p-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
      <span className="ml-2 text-xs text-[#837f90]">Searching...</span>
    </div>
  );

  const NoResultsMessage = ({ searchTerm }) => (
    <div className="p-3 text-center">
      <div className="text-xs text-[#837f90] mb-1">
        No conditions found for "{searchTerm}"
      </div>
      <div className="text-xs text-[#837f90] opacity-75">
        Try a different search term or check spelling
      </div>
    </div>
  );

  const resetForm = () => {
    setForm({
      condition: "",
      selectedRelations: [],
    });
    setConditionSearch("");
    setEditingId(null);
  };

  const handleAddCondition = async () => {
    if (
      !form.condition ||
      !form.selectedRelations ||
      form.selectedRelations.length === 0
    ) {
      setShowValidationModal(true);
      return false;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Create one backend record per selected relation (backend unchanged)
      const relationsToSave = form.selectedRelations.filter(
        (r) => r && r.trim(),
      ); // Filter out empty values

      // Map granular relations to backend-supported enum values
      const mapRelationForApi = (value) => {
        switch (value) {
          case "paternal_grandmother":
          case "maternal_grandmother":
            return "grandmother";
          case "paternal_grandfather":
          case "maternal_grandfather":
            return "grandfather";
          case "maternal_aunt":
          case "paternal_aunt":
            return "aunt";
          case "maternal_uncle":
          case "paternal_uncle":
            return "uncle";
          default:
            return value; // brother, sister, mother, father, etc.
        }
      };

      if (editingId) {
        // For editing: delete all existing entries for this condition group, then add new ones
        const conditionToEdit = chronicles.find(
          (item) => item.id === editingId,
        );
        if (!conditionToEdit) {
          throw new Error("Condition not found");
        }

        // Get all IDs for this condition
        const idsToDelete = chronicles
          .filter((item) => item.condition === conditionToEdit.condition)
          .map((item) => item.id);

        // Delete all existing entries for this condition
        for (const id of idsToDelete) {
          await deleteFamilyHistory(id, token);
        }

        // Remove from local state
        setChronicles((prev) =>
          prev.filter((item) => item.condition !== conditionToEdit.condition),
        );

        // Add new entries with updated relations
        const results = { succeeded: [], failed: [] };
        for (const relation of relationsToSave) {
          try {
            // Ensure relation value is not empty
            const relationValue = mapRelationForApi(relation.trim());
            if (!relationValue) {
              results.failed.push({
                relation,
                error: "Relation value is empty",
              });
              continue;
            }

            const familyHistoryData = {
              condition: form.condition.trim(),
              relation: relationValue, // Backend API expects 'relation' field as shown in Postman
            };

            const response = await addFamilyHistory(familyHistoryData, token);
            if (response.success) {
              const newCondition = {
                condition: form.condition.trim(),
                relation,
                id: response.data._id || response.data.id,
                status: "active",
              };
              setChronicles((prev) => [...prev, newCondition]);
              results.succeeded.push(relation);
            } else {
              const errorMsg = response.message || "Failed to add relation";
              results.failed.push({ relation, error: errorMsg });
              console.error(`Failed to add relation "${relation}":`, errorMsg);
            }
          } catch (error) {
            const errorMsg = error.message || "Failed to add relation";
            results.failed.push({ relation, error: errorMsg });
            console.error(`Error adding relation "${relation}":`, error);
          }
        }

        // Show appropriate message based on results
        if (results.succeeded.length > 0 && results.failed.length === 0) {
          setSuccessMessage("Family history updated successfully!");
        } else if (results.succeeded.length > 0 && results.failed.length > 0) {
          const succeededLabels = results.succeeded
            .map((r) => relationOptions.find((o) => o.value === r)?.label || r)
            .join(", ");
          const failedLabels = results.failed
            .map(
              (f) =>
                relationOptions.find((o) => o.value === f.relation)?.label ||
                f.relation,
            )
            .join(", ");
          setSuccessMessage(
            `Updated: ${succeededLabels}. Failed: ${failedLabels}`,
          );
          setError(
            `Some relations failed to save. Please check the relation values with your administrator.`,
          );
          // Clear error after showing for a bit, but still proceed
          setTimeout(() => setError(""), 10000);
        } else {
          throw new Error(
            "All relations failed to save. Please check the relation values.",
          );
        }
      } else {
        // Add multiple entries (one per relation)
        const results = { succeeded: [], failed: [] };
        for (const relation of relationsToSave) {
          try {
            // Ensure relation value is not empty
            const relationValue = mapRelationForApi(relation.trim());
            if (!relationValue) {
              results.failed.push({
                relation,
                error: "Relation value is empty",
              });
              continue;
            }

            const familyHistoryData = {
              condition: form.condition.trim(),
              relation: relationValue, // Backend API expects 'relation' field as shown in Postman
            };

            const response = await addFamilyHistory(familyHistoryData, token);
            if (response.success) {
              const newCondition = {
                condition: form.condition.trim(),
                relation,
                id: response.data._id || response.data.id,
                status: "active",
              };
              setChronicles((prev) => [...prev, newCondition]);
              results.succeeded.push(relation);
            } else {
              const errorMsg = response.message || "Failed to add relation";
              results.failed.push({ relation, error: errorMsg });
              console.error(`Failed to add relation "${relation}":`, errorMsg);
            }
          } catch (error) {
            const errorMsg = error.message || "Failed to add relation";
            results.failed.push({ relation, error: errorMsg });
            console.error(`Error adding relation "${relation}":`, error);
          }
        }

        // Show appropriate message based on results
        if (results.succeeded.length > 0 && results.failed.length === 0) {
          setSuccessMessage("Family history added successfully!");
        } else if (results.succeeded.length > 0 && results.failed.length > 0) {
          const succeededLabels = results.succeeded
            .map((r) => relationOptions.find((o) => o.value === r)?.label || r)
            .join(", ");
          const failedLabels = results.failed
            .map(
              (f) =>
                relationOptions.find((o) => o.value === f.relation)?.label ||
                f.relation,
            )
            .join(", ");
          setSuccessMessage(
            `Added: ${succeededLabels}. Failed: ${failedLabels}`,
          );
          setError(
            `Some relations failed to save. The backend may not support these relation values yet. Failed: ${failedLabels}`,
          );
          // Clear error after showing for a bit, but still proceed
          setTimeout(() => setError(""), 10000);
        } else {
          throw new Error(
            "All relations failed to save. Please check the relation values.",
          );
        }
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
      resetForm();
      return true; // Return true to indicate success
    } catch (error) {
      console.error("Error saving family history:", error);
      setError(
        error.message || "Failed to save family history. Please try again.",
      );
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCondition = (group) => {
    // Get all relations for this condition group
    const allRelationsForCondition = chronicles
      .filter((item) => item.condition === group.condition)
      .map((item) => item.relation);

    setForm({
      condition: group.condition || "",
      selectedRelations: allRelationsForCondition,
    });
    setConditionSearch(group.condition || "");
    setEditingId(group.ids[0]); // Use first ID as editing identifier
  };

  const handleDeleteCondition = (group) => {
    setDeleteItemId(group); // Store the group instead of just ID
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId || !deleteItemId.ids) return;

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Delete all entries for this condition group
      for (const id of deleteItemId.ids) {
        const response = await deleteFamilyHistory(id, token);
        if (!response.success) {
          throw new Error(
            response.message || "Failed to delete family history",
          );
        }
      }

      // Remove from local state
      setChronicles((prev) =>
        prev.filter((item) => item.condition !== deleteItemId.condition),
      );
      setSuccessMessage("Family history deleted successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting family history:", error);
      setError(
        error.message || "Failed to delete family history. Please try again.",
      );
    } finally {
      setIsSaving(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  // Filtered chronicles
  const filteredChronicles = chronicles.filter((item) => {
    const matchesSearch =
      item.condition?.toLowerCase().includes(search.toLowerCase()) ||
      item.relation?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group by condition for easy viewing
  const groupedByCondition = Object.values(
    filteredChronicles.reduce((acc, item) => {
      const key = item.condition || "";
      if (!acc[key]) {
        acc[key] = { condition: key, relations: [], ids: [] };
      }
      acc[key].relations.push(item.relation);
      acc[key].ids.push(item.id);
      return acc;
    }, {}),
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Family History Medical Conditions */}
          <AccountSection>
            {editingId
              ? t("editCondition", lang) || "Edit Medical Condition"
              : t("familyHistoryMedicalConditions", lang)}
          </AccountSection>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-green-700 text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("searchConditions", lang)} *
              </label>
              <div className="relative" ref={conditionDropdownRef}>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    name="condition"
                    value={form.condition}
                    disabled={dontHaveAccess}
                    onChange={handleConditionSearchChange}
                    placeholder={t("searchConditionsPlaceholder", lang)}
                    className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  />
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[#f7f8fa] border border-gray-200">
                    {isConditionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#837f90"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                    )}
                  </span>
                </div>

                {/* Condition Search Dropdown */}
                {showConditionDropdown && (
                  <div className="absolute left-0 top-12 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-64 overflow-y-auto">
                    {isConditionLoading ? (
                      <LoadingSpinner />
                    ) : conditionResults.length > 0 ? (
                      conditionResults.map((condition, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0"
                          onClick={() => handleConditionSelect(condition)}
                        >
                          <div className="font-semibold text-[#2d348f]">
                            {condition.consumerName || condition.primaryName}
                          </div>
                          <div className="text-xs text-[#837f90]">
                            {condition.icd9Code &&
                              `ICD-9: ${condition.icd9Code}`}
                            {condition.icd10cmCodes &&
                              condition.icd9Code &&
                              " • "}
                            {condition.icd10cmCodes &&
                              `ICD-10: ${condition.icd10cmCodes}`}
                          </div>
                          {condition.icd9Text && (
                            <div className="text-xs text-[#837f90] italic">
                              {condition.icd9Text}
                            </div>
                          )}
                        </button>
                      ))
                    ) : conditionSearch.length >= 2 ? (
                      <NoResultsMessage searchTerm={conditionSearch} />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("familyRelation", lang)} *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 py-1">
                {relationOptions
                  .filter((o) => o.value)
                  .map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.selectedRelations.includes(opt.value)}
                        disabled={dontHaveAccess}
                        onChange={() => handleRelationToggle(opt.value)}
                        className="w-4 h-4 text-[#4962d8] border-gray-300 rounded focus:ring-[#4962d8] focus:ring-2"
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
              </div>
            </div>
          </div>

          {/* Form Action Buttons */}
          <div className="flex items-center justify-center mb-8">
            {editingId ? (
              <div className="flex gap-4">
                <AccountButton
                  className="px-6 py-3"
                  onClick={handleAddCondition}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </div>
                  ) : (
                    t("updateCondition", lang) || "Update Condition"
                  )}
                </AccountButton>
                <AccountButton className="px-6 py-3" onClick={handleCancelEdit}>
                  {t("cancel", lang) || "Cancel"}
                </AccountButton>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <span className="text-sm text-[#837f90] font-medium">
                  {t("addConditionToHistory", lang) ||
                    "Add condition to family history"}
                </span>
                <button
                  onClick={handleAddCondition}
                  disabled={isSaving || dontHaveAccess}
                  className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-[#4962d8] to-[#6b46c1] text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#4962d8] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  title={t("addCondition", lang) || "Add Condition"}
                >
                  {isSaving ? (
                    <svg
                      className="animate-spin h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => handleAddCondition()}
              disabled={isSaving || dontHaveAccess}
            >
              {isSaving ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                t("save", lang)
              )}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <>
                {t("next", lang)} <span className="ml-2">&raquo;</span>
              </>
            </AccountButton>
          </div>

          {/* My Chronicles Table */}
          <AccountSection>{t("myChronicles", lang)}</AccountSection>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
            <div className="flex-1 flex items-center gap-2">
              <input
                id="chronicle-search"
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t("searchPlaceholder", lang)}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                style={{ maxWidth: 200 }}
              />
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#837f90"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              style={{ maxWidth: 140 }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto max-w-xs sm:max-w-full">
            <table className="bg-white text-sm min-w-full">
              <thead>
                <tr className="bg-[#f7f8fa] text-[#837f90]">
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("tableCondition", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("tableRelation", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("actions", lang) || "Actions"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-6 w-6 text-[#4962d8]"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-[#837f90]">
                          Loading family history...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredChronicles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-[#837f90]">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium mb-2">
                          No family history added yet
                        </p>
                        <p className="text-sm text-gray-500">
                          Add your first family history condition using the form
                          above
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  groupedByCondition.map((group, idx) => (
                    <tr
                      key={group.condition + idx}
                      className="border-t border-gray-100"
                    >
                      <td className="px-4 py-2">{group.condition}</td>
                      <td className="px-4 py-2">
                        {group.relations
                          .map(
                            (rel) =>
                              relationOptions.find((o) => o.value === rel)
                                ?.label || rel,
                          )
                          .join(", ")}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditCondition(group)}
                            className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-600 rounded hover:bg-blue-50 transition-colors"
                            title={t("edit", lang) || "Edit"}
                          >
                            {t("edit", lang) || "Edit"}
                          </button>
                          <button
                            onClick={() => handleDeleteCondition(group)}
                            className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-600 rounded hover:bg-red-50 transition-colors"
                            title={t("delete", lang) || "Delete"}
                          >
                            {t("delete", lang) || "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("validationError", lang) || "Validation Error"}
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                {t("pleaseFillRequiredFields", lang) ||
                  "Please fill in all required fields"}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowValidationModal(false)}
                className="bg-[#4962d8] text-white px-4 py-2 rounded-md hover:bg-[#3d4fc7] transition-colors"
              >
                {t("ok", lang) || "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("confirmDelete", lang) || "Confirm Delete"}
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                {t("deleteConditionWarning", lang) ||
                  "Are you sure you want to delete this condition? This action cannot be undone."}
                {deleteItemId && deleteItemId.condition && (
                  <span className="block mt-2 font-semibold text-gray-700">
                    Condition: {deleteItemId.condition}
                  </span>
                )}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteItemId(null);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                {t("cancel", lang) || "Cancel"}
              </button>
              <button
                onClick={confirmDelete}
                disabled={isSaving}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Deleting...
                  </div>
                ) : (
                  t("delete", lang) || "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
